/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2026 (original work) Open Assessment Technologies SA
 */

import { get } from 'svelte/store';
import { appLocaleFallback, locale } from '@/core/utils/locale';
import { accessToken as accessTokenStore } from '@/store/accessToken';
import { configurationStore } from '@/store/tenantConfiguration';

const VISIBLE_POLL_INTERVAL_MS = 5_000;

let refreshPromise = null;
let scheduledRefreshId = null;
let pollIntervalId = null;

/**
 * Re-fetches tenant configuration and reapplies date format settings.
 * Concurrent calls share the same in-flight request.
 * @returns {Promise<void>}
 */
export function refreshTenantConfiguration() {
    if (!get(accessTokenStore)) {
        return Promise.resolve();
    }

    if (refreshPromise) {
        return refreshPromise;
    }

    const code = locale.getApplicationLocale() || appLocaleFallback;

    refreshPromise = configurationStore
        .updateConfigurationData(code)
        .catch(error => {
            // eslint-disable-next-line no-console
            console.error('[proctoring] failed to refresh tenant configuration', error);
            throw error;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
}

/**
 * Coalesces rapid visibility/focus events into a single refresh on the next tick.
 */
function scheduleTenantConfigurationRefresh() {
    if (scheduledRefreshId !== null) {
        return;
    }

    scheduledRefreshId = setTimeout(() => {
        scheduledRefreshId = null;
        refreshTenantConfiguration();
    }, 0);
}

function startVisiblePolling() {
    if (pollIntervalId !== null || document.visibilityState !== 'visible') {
        return;
    }

    pollIntervalId = setInterval(() => {
        refreshTenantConfiguration();
    }, VISIBLE_POLL_INTERVAL_MS);
}

function stopVisiblePolling() {
    if (pollIntervalId === null) {
        return;
    }

    clearInterval(pollIntervalId);
    pollIntervalId = null;
}

function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
        scheduleTenantConfigurationRefresh();
        startVisiblePolling();
        return;
    }

    stopVisiblePolling();
}

/**
 * Registers listeners that refresh tenant configuration when the iframe becomes active again.
 */
export function registerTenantConfigurationRefreshListeners() {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', scheduleTenantConfigurationRefresh);
    window.addEventListener('pageshow', scheduleTenantConfigurationRefresh);

    if (document.visibilityState === 'visible') {
        startVisiblePolling();
    }
}

/**
 * Stops background refresh listeners. Intended for tests.
 */
export function unregisterTenantConfigurationRefreshListeners() {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', scheduleTenantConfigurationRefresh);
    window.removeEventListener('pageshow', scheduleTenantConfigurationRefresh);
    stopVisiblePolling();

    if (scheduledRefreshId !== null) {
        clearTimeout(scheduledRefreshId);
        scheduledRefreshId = null;
    }
}
